import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ReCreditButton } from "@/components/ReCreditButton";
import { ReAddButton } from "@/components/ReAddButton";
import { RescheduleSession } from "@/components/RescheduleSession";
import { AwaitingFeedbackList } from "@/components/AwaitingFeedbackList";
import { PlayerResolutionPanel, ResolutionBadge } from "@/components/PlayerResolutionPanel";
import { editLockReason } from "@/pages/OfferingNew";
import { SessionAssessmentActions } from "@/components/SessionAssessmentActions";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, MapPin, Users, UserPlus, Search, Loader2, CreditCard, Ticket, BellRing, Check, Undo2, Lock, UserCircle, StickyNote } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}`);
  return d.toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const PAY_LABEL = {
  paid: ["Paid", "#22C55E"], free: ["Paid · free", "#22C55E"], credit: ["Paid · credit", "#22C55E"],
  awaiting_payment: ["Unpaid", "#FF4500"], unknown: ["Status unknown", "#737373"],
};

function PaymentSummary({ roster }) {
  const unpaid = roster.filter((p) => p.payment_status === "awaiting_payment");
  const paid = roster.filter((p) => ["paid", "free", "credit"].includes(p.payment_status));
  const owed = unpaid.reduce((t, p) => t + Number(p.amount_outstanding || 0), 0);
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mb-3" data-testid="payment-summary">
      <span className="text-[#22C55E] font-semibold" data-testid="payment-summary-paid">{paid.length} paid</span>
      <span className={unpaid.length ? "text-[#FF4500] font-semibold" : "text-[#A3A3A3]"} data-testid="payment-summary-unpaid">{unpaid.length} unpaid</span>
      <span className={owed ? "text-[#FF4500] font-semibold" : "text-[#A3A3A3]"} data-testid="payment-summary-outstanding">£{owed.toFixed(2)} outstanding</span>
    </div>
  );
}

const METHOD_LABEL = { cash: "Paid (cash)", bank_transfer: "Paid (bank transfer)" };

function UnpaidActions({ sessionId, p, onChanged }) {
  const [busy, setBusy] = useState("");
  const [choosing, setChoosing] = useState(false);
  const run = async (key, path, body, msg) => {
    setBusy(key);
    try {
      await api.post(`/offerings/${sessionId}/players/${p.id}/${path}`, body);
      toast.success(msg);
      if (path === "mark-paid") onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(""); setChoosing(false); }
  };
  const btn = "px-2.5 py-1 text-[11px] uppercase tracking-wider font-bold border disabled:opacity-40 inline-flex items-center gap-1";
  return (
    <div className="flex flex-wrap justify-end gap-1.5 mt-1.5">
      <button type="button" disabled={!!busy} onClick={() => run("remind", "remind", {}, `Reminder sent to ${p.name}`)}
        className={`${btn} border-[#FF9F1C] text-[#FF9F1C]`} data-testid={`roster-remind-${p.id}`}>
        {busy === "remind" ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />} Remind
      </button>
      {choosing ? (
        <>
          <button type="button" disabled={!!busy} onClick={() => run("cash", "mark-paid", { method: "cash" }, `${p.name} marked paid (cash)`)}
            className={`${btn} border-[#22C55E] text-[#22C55E]`} data-testid={`roster-paid-cash-${p.id}`}>Cash</button>
          <button type="button" disabled={!!busy} onClick={() => run("bank", "mark-paid", { method: "bank_transfer" }, `${p.name} marked paid (bank transfer)`)}
            className={`${btn} border-[#22C55E] text-[#22C55E]`} data-testid={`roster-paid-bank-${p.id}`}>Bank transfer</button>
          <button type="button" onClick={() => setChoosing(false)} className="text-[11px] text-[#A3A3A3] px-1" data-testid={`roster-paid-cancel-${p.id}`}>Cancel</button>
        </>
      ) : (
        <button type="button" disabled={!!busy} onClick={() => setChoosing(true)}
          className={`${btn} border-[#404040] text-[#E5E5E5] hover:border-[#22C55E]`} data-testid={`roster-mark-paid-${p.id}`}>
          <Check className="w-3 h-3" /> Mark as paid
        </button>
      )}
    </div>
  );
}

function UndoPaid({ sessionId, p, onChanged }) {
  const [busy, setBusy] = useState(false);
  const undo = async () => {
    setBusy(true);
    try {
      await api.post(`/offerings/${sessionId}/players/${p.id}/undo-paid`);
      toast.success(`${p.name} is back to unpaid`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't undo"); setBusy(false); }
  };
  return (
    <button type="button" onClick={undo} disabled={busy} data-testid={`roster-undo-paid-${p.id}`}
      className="mt-1 text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 disabled:opacity-40">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Undo
    </button>
  );
}

function RemindAll({ sessionId, count }) {
  const [busy, setBusy] = useState(false);
  if (!count) return null;
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/offerings/${sessionId}/remind-unpaid`);
      toast.success(`${data.sent} reminder${data.sent === 1 ? "" : "s"} sent`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't send reminders"); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid="remind-all-unpaid"
      className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold border border-[#FF9F1C] text-[#FF9F1C] inline-flex items-center gap-1.5 disabled:opacity-40">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />} Remind all unpaid ({count})
    </button>
  );
}

function RosterRow({ p, sessionId, onChanged, oneOnOne, readOnly, isPast, session }) {
  const [label, color] = (p.payment_status === "paid" && METHOD_LABEL[p.payment_method] && [METHOD_LABEL[p.payment_method], "#22C55E"])
    || PAY_LABEL[p.payment_status] || [p.payment_status, "#A3A3A3"];
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-[#1F1F1F]" data-testid={`roster-row-${p.id}`}>
      <div className="min-w-0">
        <Link to={`/players/${p.id}`} className="text-sm font-semibold hover:text-[#CCFF00]">{p.name}</Link>
        <div className="text-xs text-[#737373] truncate">{p.email}{p.added_by_coach ? " · added by coach" : ""}</div>
        {isPast && <div className="mt-0.5"><ResolutionBadge p={p} /></div>}
        {p.needs_assessment && p.booking_id && (
          <SessionAssessmentActions p={p} sessionId={sessionId} sessionTitle={session?.title} sport={session?.sport} onChanged={onChanged} />
        )}
      </div>
      <div className="text-right shrink-0">
        <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color }} data-testid={`roster-pay-${p.id}`}>{label}</span>
        {p.payment_status === "paid" && METHOD_LABEL[p.payment_method] && !readOnly && <div><UndoPaid sessionId={sessionId} p={p} onChanged={onChanged} /></div>}
        {!readOnly && <div className="mt-1.5"><ReCreditButton studentId={p.id} studentName={p.name} bookingId={p.booking_id} onDone={onChanged} testid={`roster-recredit-${p.id}`} /></div>}
        {oneOnOne && p.booking_id && !readOnly && (
          <div className="mt-1.5 flex justify-end text-left"><RescheduleSession bookingId={p.booking_id} staff onDone={onChanged} testid={`roster-reschedule-${p.id}`} /></div>
        )}
        {p.payment_status === "awaiting_payment" && (
          <>
            <div className="text-xs text-[#FF4500]" data-testid={`roster-owed-${p.id}`}>£{Number(p.amount_outstanding || 0).toFixed(2)} outstanding</div>
            <UnpaidActions sessionId={sessionId} p={p} onChanged={onChanged} />
          </>
        )}
      </div>
    </div>
  );
}

function AddPlayer({ session, onAdded }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState("");
  const paid = Number(session.price_per_player || 0) > 0;

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/coach/enrol-search?q=${encodeURIComponent(q.trim())}&offering_id=${session.id}`).then((r) => setResults(r.data || [])).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, session.id]);

  const add = async (p, payment) => {
    setBusy(`${p.id}-${payment}`);
    try {
      await api.post(`/offerings/${session.id}/coach-enrol`, { student_id: p.id, payment });
      toast.success(`${p.name} added — they've been emailed and notified`);
      setQ(""); setResults([]); onAdded();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't add player"); }
    finally { setBusy(""); }
  };

  return (
    <div className="card-surface p-5 space-y-3" data-testid="add-player-panel">
      <div className="label-mono inline-flex items-center gap-2"><UserPlus className="w-3.5 h-3.5 text-[#CCFF00]" /> ADD A PLAYER</div>
      {session.spaces_left <= 0 ? (
        <div className="text-sm text-[#A3A3A3]" data-testid="add-player-full">This session is full.</div>
      ) : (
        <>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players by name or email"
              className="w-full bg-[#141414] border border-[#262626] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" data-testid="add-player-search" />
          </div>
          <div className="space-y-1">
            {results.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-[#1F1F1F]" data-testid={`add-player-result-${p.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{p.name}</div>
                  <div className="text-xs text-[#737373]">{p.email} · {p.credits} credit{p.credits === 1 ? "" : "s"}</div>
                </div>
                {p.already_enrolled ? <span className="text-xs text-[#A3A3A3]">Already enrolled</span> : !paid ? (
                  <button type="button" onClick={() => add(p, "link")} disabled={!!busy} data-testid={`add-player-free-${p.id}`}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black disabled:opacity-40">Add</button>
                ) : (
                  <div className="flex gap-2">
                    <button type="button" onClick={() => add(p, "link")} disabled={!!busy} data-testid={`add-player-link-${p.id}`}
                      className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-1 disabled:opacity-40">
                      {busy === `${p.id}-link` ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />} Send payment link
                    </button>
                    <button type="button" onClick={() => add(p, "credit")} disabled={!!busy || p.credits < 1} data-testid={`add-player-credit-${p.id}`}
                      title={p.credits < 1 ? "No credits available" : "Uses one of their credits"}
                      className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold border border-[#66C2FF] text-[#66C2FF] inline-flex items-center gap-1 disabled:opacity-30">
                      <Ticket className="w-3 h-3" /> Used credit
                    </button>
                  </div>
                )}
              </div>
            ))}
            {q.trim().length >= 2 && results.length === 0 && <div className="text-xs text-[#737373]">No players match.</div>}
          </div>
        </>
      )}
    </div>
  );
}

export default function SessionDetail() {
  const { user } = useAuth();
  if (user && !["coach", "admin"].includes(user.role)) return <Navigate to="/offerings" replace />;
  return <CoachSessionDetail />;
}

function CoachSessionDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get(`/offerings/${id}/detail`).then((r) => setS(r.data)).catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "Session not found"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="p-8 text-sm text-[#FF4500]" data-testid="session-detail-error">{err}</div>;
  if (!s) return <div className="p-8 text-sm text-[#A3A3A3] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading session…</div>;
  const lock = editLockReason({ ...s, enrolled_student_ids: s.roster });
  return (
    <div className="space-y-6 max-w-3xl" data-testid="session-detail-page">
      <Link to="/offerings" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1" data-testid="session-detail-back"><ArrowLeft className="w-3.5 h-3.5" /> My offerings</Link>
      <div>
        <div className="label-mono text-[#CCFF00] mb-1">{s.sport} · {String(s.session_type || "").replace(/_/g, " ")}</div>
        <h1 className="h-display text-4xl sm:text-5xl" data-testid="session-detail-title">{s.title}</h1>
        <div className="mt-3 flex flex-wrap gap-2">
          {lock ? (
            <Link to={`/offerings/new?copy_id=${s.id}`} data-testid="session-detail-copy"
              className="min-h-[40px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-1.5">Copy as new session</Link>
          ) : (
            <Link to={`/offerings/new?edit_id=${s.id}`} data-testid="session-detail-edit"
              className="min-h-[40px] px-4 py-2 text-xs uppercase tracking-wider border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5">Edit session</Link>
          )}
        </div>
        {lock && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 border border-[#404040] text-[11px] uppercase tracking-wider text-[#A3A3A3]" data-testid="session-detail-readonly">
            <Lock className="w-3.5 h-3.5" /> Read-only · {lock}
          </div>
        )}
        <div className="mt-3 space-y-1 text-sm text-[#E5E5E5]">
          <div className="flex items-center gap-2" data-testid="session-detail-coach"><UserCircle className="w-4 h-4 text-[#A3A3A3]" /> Coach: {s.coach_name || "—"}</div>
          <div className="inline-flex items-center gap-2" data-testid="session-detail-when"><CalendarDays className="w-4 h-4 text-[#A3A3A3]" /> {fmtWhen(s.start)} · {s.duration_min} min</div>
          <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#A3A3A3]" /> {s.venue || "Venue TBC"}</div>
          <div className="flex items-center gap-2" data-testid="session-detail-spaces"><Users className="w-4 h-4 text-[#A3A3A3]" /> {s.roster.length}/{s.max_spaces} enrolled · £{Number(s.price_per_player || 0).toFixed(2)} per player</div>
        </div>
      </div>
      {lock && (
        <div className="card-surface p-5" data-testid="session-detail-notes">
          <div className="label-mono mb-2 inline-flex items-center gap-2"><StickyNote className="w-3.5 h-3.5 text-[#CCFF00]" /> SESSION NOTES</div>
          <div className="text-sm text-[#E5E5E5] whitespace-pre-wrap">{s.notes || <span className="text-[#737373]">No notes for this session.</span>}</div>
        </div>
      )}
      {s.has_ended
        ? <PlayerResolutionPanel session={s} onChanged={load} highlight={new URLSearchParams(window.location.search).get("feedback") === "1"} />
        : <AwaitingFeedbackList roster={s.roster} highlight={new URLSearchParams(window.location.search).get("feedback") === "1"} />}
      <div className="card-surface p-5" data-testid="session-roster">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="label-mono">PLAYERS &amp; PAYMENTS</div>
          <RemindAll sessionId={s.id} count={s.roster.filter((p) => p.payment_status === "awaiting_payment").length} />
        </div>
        {s.roster.length > 0 && <PaymentSummary roster={s.roster} />}
        {s.roster.length ? s.roster.map((p) => <RosterRow key={p.id} p={p} sessionId={s.id} session={s} onChanged={load} oneOnOne={s.session_type === "one_on_one"} readOnly={!!lock} isPast={!!s.has_ended} />) : <div className="text-sm text-[#737373]">No one enrolled yet.</div>}
        {(s.cancelled || []).length > 0 && (
          <div className="mt-4" data-testid="session-cancelled-players">
            <div className="label-mono text-[#A3A3A3] mb-1">CANCELLED</div>
            {s.cancelled.map((c) => (
              <div key={c.booking_id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-[#1F1F1F]" data-testid={`cancelled-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-[#A3A3A3]">
                    {c.name}{c.removed_by_coach ? ` · removed by ${c.cancelled_by_name || "coach"}` : c.late ? " · late cancellation" : ""}{c.paid_by_credit ? " · paid by credit" : ""}
                  </div>
                  {c.reason && !c.removed_by_coach && (
                    <div className="text-xs text-[#E5E5E5] mt-0.5 italic" data-testid={`cancelled-reason-${c.id}`}>Reason: “{c.reason}”</div>
                  )}
                </div>
                {!lock && <div className="flex flex-wrap items-center gap-2">
                  {c.credit_returned
                    ? <span className="text-[11px] uppercase tracking-wider text-[#22C55E]" data-testid={`cancelled-credited-${c.id}`}>Credit returned</span>
                    : <ReCreditButton studentId={c.id} studentName={c.name} bookingId={c.booking_id} onDone={load} testid={`cancelled-recredit-${c.id}`} />}
                  <ReAddButton sessionId={s.id} c={c} full={s.spaces_left <= 0} price={s.price_per_player} onDone={load} />
                </div>}
              </div>
            ))}
          </div>
        )}
      </div>
      {!lock && <AddPlayer session={s} onAdded={load} />}
    </div>
  );
}
